import type { MessagePayload } from "firebase/messaging";
import type { RouteLocationRaw } from "vue-router";
import type { HeadlessProgressToastProps } from "../../components/Sonner/SonnerCustom";
import { readPushNotificationData } from "./pushNotificationData";
import { inboxNotificationRoute } from "./v2/inboxNotificationLink";

type ToastButton = NonNullable<HeadlessProgressToastProps["buttons"]>[number];

const VIEW_LABEL_KEYS = {
  task: "navbar.dropdown.view_task",
  report: "navbar.dropdown.view_report",
  reservation: "navbar.dropdown.view_reservation",
} as const;

export interface PushMessageHandlerDependencies {
  currentUserIri: () => string | null
  selectedLocation: () => string | null
  /** Whether notifications v2 updates arrive over Mercure right now. */
  isLive: () => boolean
  /** Refreshes the navbar count of the active notification system. */
  checkUnreadCount: () => Promise<unknown>
  /** Reloads the v2 unread count and the open inbox lists, for when Mercure is down. */
  resync: () => Promise<unknown>
  hasEntity: (id: number, type: "task" | "report") => boolean
  markNotificationAsReadById: (notificationId: number, isUserNotification?: boolean) => Promise<unknown>
  markNotificationsAsReadByEntity: (entityIri: string, entityType: "task" | "report") => Promise<unknown>
  navigate: (location: RouteLocationRaw) => unknown
  translate: (key: string) => string
  showToast: (props: HeadlessProgressToastProps) => void
}

/**
 * Handles a web push that arrives while Verbleif is open in the foreground: keeps the count current when nothing
 * else does, and shows a toast whose button opens the push's screen and reads the notification it names.
 */
export function createPushMessageHandler(deps: PushMessageHandlerDependencies) {
  return function handlePushMessage(payload: MessagePayload): void {
    const { notification, data } = payload;

    if (!notification) {
      throw new Error("Invalid payload.notification received from firebase.");
    }
    if (!data) {
      throw new Error("Invalid payload.data received from firebase.");
    }

    // Check if message is targeted to current user
    if ("target" in data && data.target !== deps.currentUserIri()) {
      return;
    }

    // Only show a notification of the selected location.
    const currentLocation = deps.selectedLocation();
    if (typeof data.location === "string" && data.location && currentLocation && data.location !== currentLocation) {
      return;
    }

    if (!notification.title || typeof notification.title !== "string") {
      throw new Error("Invalid payload.notification.title received from firebase.");
    }
    if (!notification.body) {
      throw new Error("Invalid payload.notification.body received from firebase.");
    }

    const { isUserNotification, notificationId, target } = readPushNotificationData(data);

    // A notifications v2 change also reaches this tab over Mercure; only without it the count and lists reload.
    if (!isUserNotification) {
      void deps.checkUnreadCount();
    } else if (!deps.isLive()) {
      void deps.resync();
    }

    // Don't show a notification about a task or report this user just created.
    if (target && (target.type === "task" || target.type === "report") && deps.hasEntity(target.id, target.type)) {
      return;
    }

    const buttons: ToastButton[] = [];
    if (target && (target.type === "task" || target.type === "report" || target.type === "reservation")) {
      const reportIri = !isUserNotification && target.type === "report" && typeof data.report === "string" ? data.report : null;
      buttons.push({
        variant: "primary",
        size: "small",
        label: deps.translate(VIEW_LABEL_KEYS[target.type]),
        iconLeft: "eye",
        onClick: () => {
          // Opening the push reads the notification it names; both calls log their own failures.
          if (notificationId) {
            void deps.markNotificationAsReadById(notificationId, isUserNotification);
          } else if (reportIri) {
            // A legacy report push without an id: read every notification of that report instead.
            void deps.markNotificationsAsReadByEntity(reportIri, "report");
          }
          deps.navigate(inboxNotificationRoute(target));
        },
      });
    } else if (target?.type === "quiet_hours_summary") {
      buttons.push({
        variant: "primary",
        size: "small",
        label: deps.translate("navbar.dropdown.view_quiet_hours_summary"),
        iconLeft: "inbox",
        onClick: () => {
          deps.navigate(inboxNotificationRoute(target));
        },
      });
    }

    deps.showToast({
      title: notification.title ?? "",
      message: notification.body ?? "",
      notificationIcon: "bell",
      buttons: buttons.length > 0 ? buttons : undefined,
    });
  };
}
